import axios from "axios";
import { API } from "@/constants";

const TOKEN_KEY = "sparkcue_beta_token";
const ANON_KEY = "sparkcue_anon_session";

// Batch 122 — the anonymous session id the screenshot cap counts against.
//
// sessionStorage, not localStorage: "per session, not per device" is the ruling,
// and sessionStorage is exactly that -- it survives in-app navigation and a full
// reload within the tab, and dies when the tab closes. Someone opening a fresh
// tab for two more parses is doing more work than registering.
//
// The client mints the key; the SERVER counts and refuses. This value is not a
// credential and grants nothing: tampering with it buys at most a reset of a
// two-parse allowance, which is the same thing closing the tab already does.
export const getAnonSession = () => {
  try {
    let v = sessionStorage.getItem(ANON_KEY);
    if (!v) {
      v = `anon_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      sessionStorage.setItem(ANON_KEY, v);
    }
    return v;
  } catch {
    // Private mode or blocked storage. A stable-per-load fallback still lets the
    // server count something rather than seeing no key at all.
    return null;
  }
};

// Batch 124 — the two codes that mean "policy said no", declared ONCE.
//
// TWO readers must agree exactly: the interceptor below raises a modal for
// these, and App.js's postGenerate bails out of the generate for these. A code
// in one list and not the other is a silent failure in one direction or the
// other -- an error screen sitting behind a paywall, or a tap that produces
// nothing at all. Neither reader keeps its own copy of the literals.
export const QUOTA_ERROR_CODES = ["daily_limit_reached", "free_limit_reached"];

export const isQuotaRejection = (err) =>
  err?.response?.status === 429
  && QUOTA_ERROR_CODES.includes(err?.response?.data?.detail?.error);

export const getBetaToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

function applyAuthHeader() {
  const t = getBetaToken();
  if (t) axios.defaults.headers.common["Authorization"] = `Bearer ${t}`;
  else delete axios.defaults.headers.common["Authorization"];
}

export const setBetaToken = (t) => {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
  applyAuthHeader();
};

export const clearBetaToken = () => setBetaToken(null);

applyAuthHeader();

let _ready = false;
export function initBetaInterceptors() {
  if (_ready) return;
  _ready = true;
  // Batch 122 — the anonymous session id rides every request. The screenshot cap
  // is the only reader today; sending it unconditionally keeps the client from
  // having to know which routes count.
  axios.interceptors.request.use((config) => {
    const a = getAnonSession();
    if (a) config.headers["X-Anon-Session"] = a;
    return config;
  });
  axios.interceptors.response.use(
    (res) => {
      try {
        if (res.config?.url && /\/generate-reply(\/async)?$/.test(res.config.url)) {
          window.dispatchEvent(new CustomEvent("beta:usage-changed"));
        }
      } catch {
        /* ignore */
      }
      return res;
    },
    (err) => {
      const st = err?.response?.status;
      const url = err?.config?.url || "";
      const detail = err?.response?.data?.detail;
      // Batch 123 — TWO EXPLICIT CODES, never a bare `st === 429`.
      //
      // The daily cap and the lifetime free tier are different rejections that
      // render different modals, one of which promises a reset, so the detail is
      // passed through and the modal branches on the code.
      //
      // DO NOT LOOSEN THIS TO ANY 429. The signup route also returns 429
      // (rate_limited, scope email/ip) and GateDoors handles that in its own
      // catch. A status-only match would pop the paywall over the registration
      // card for someone who has not spent a single turn.
      if (isQuotaRejection(err)) {
        window.dispatchEvent(new CustomEvent("beta:limit", { detail }));
      } else if (st === 401 && /\/(beta\/me|generate-reply|coaching|compress-reply)/.test(url)) {
        window.dispatchEvent(new CustomEvent("beta:session-ended"));
      } else if (st === 403 && /\/(beta\/me|generate-reply)/.test(url)) {
        window.dispatchEvent(new CustomEvent("beta:suspended"));
      }
      return Promise.reject(err);
    },
  );
}

export const betaApi = {
  config: () => axios.get(`${API}/beta/config`).then((r) => r.data),
  redeem: (body) => axios.post(`${API}/beta/redeem`, body).then((r) => r.data),
  // Batch 121 — the public door. `verify` returns the SAME shape as `redeem`
  // ({token, user, device, usage}), which is what lets both doors share
  // setBetaToken -> loadMe -> "ready" without a second code path.
  signup: (body) => axios.post(`${API}/auth/signup`, body).then((r) => r.data),
  // email AND code, never the code alone: sha256 of six digits is one of a
  // million values, so a code on its own does not identify a record.
  verify: (body) => axios.post(`${API}/auth/verify`, body).then((r) => r.data),
  me: () => axios.get(`${API}/beta/me`).then((r) => r.data),
  usage: () => axios.get(`${API}/beta/usage`).then((r) => r.data),
  // Batch 140 — checkout takes a PLAN NAME. The price ids live on the server
  // and the map is the allowlist: a client that could name a price could name
  // a cheaper one.
  checkout: (plan) => axios.post(`${API}/billing/checkout`, { plan }).then((r) => r.data),
  portal: () => axios.post(`${API}/billing/portal`).then((r) => r.data),
  devices: () => axios.get(`${API}/beta/devices`).then((r) => r.data),
  revokeDevice: (id) => axios.post(`${API}/beta/devices/${id}/revoke`).then((r) => r.data),
  // Batch 127 — the same action as revokeDevice, authorised by a live
  // verification code instead of a session, because the user at the device cap
  // has no session and that is the whole reason they are being refused.
  revokeWithCode: (body) => axios.post(`${API}/auth/devices/revoke`, body).then((r) => r.data),
};
